import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/useAuth.js';
import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { showToast } from '../components/ui/Toaster.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { getProfile, updateProfile } from '../services/authApi.js';

import { PasswordChangeCard } from './PasswordChangeCard.jsx';

const EMPTY = { address: '', zip: '', city: '', nationality: '', iban: '', birthDate: '', phone: '', photo: '' };

/** Resize an uploaded image to a small square JPEG data URL for the avatar. */
function fileToAvatar(file, size = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('not an image'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // Cover-crop: center square of the source image.
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * The profile form itself - reused by the standalone /profile page (avatar
 * menu) and as a section inside the settings page.
 */
export function ProfileForm() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const profile = useQuery({ queryKey: ['auth', 'profile'], queryFn: getProfile });

  useEffect(() => {
    if (profile.data) {
      setForm({ ...EMPTY, ...profile.data });
    }
  }, [profile.data]);

  const save = useMutation({
    mutationFn: (body) => updateProfile(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
      showToast(t('profile.saved'), 'success');
      setError(null);
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const photo = await fileToAvatar(file);
      setForm((f) => ({ ...f, photo }));
      setError(null);
    } catch {
      setError(t('profile.photoError'));
    } finally {
      e.target.value = '';
    }
  }

  function submit(e) {
    e.preventDefault();
    save.mutate({
      address: form.address.trim(),
      zip: form.zip.trim(),
      city: form.city.trim(),
      nationality: form.nationality.trim(),
      iban: form.iban.trim(),
      birthDate: form.birthDate || null,
      phone: form.phone.trim(),
      photo: form.photo || null,
    });
  }

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  return (
    <Card title={fullName}>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: 560 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {form.photo ? (
              <img src={form.photo} alt={fullName} className="profile-photo" />
            ) : (
              <div className="profile-photo profile-photo-empty">{t('profile.noPhoto')}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              <Button type="button" variant="ghost" onClick={() => fileRef.current?.click()}>
                {t('profile.uploadPhoto')}
              </Button>
              {form.photo && (
                <Button type="button" variant="ghost" onClick={() => setForm((f) => ({ ...f, photo: '' }))}>
                  {t('profile.removePhoto')}
                </Button>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
            </div>
          </div>

          <label className="field">
            <span className="field-label">{t('profile.address')}</span>
            <input className="input" value={form.address} onChange={set('address')} />
          </label>
          <div className="field-row">
            <label className="field" style={{ maxWidth: 140 }}>
              <span className="field-label">{t('profile.zip')}</span>
              <input className="input" value={form.zip} onChange={set('zip')} />
            </label>
            <label className="field">
              <span className="field-label">{t('profile.city')}</span>
              <input className="input" value={form.city} onChange={set('city')} />
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('profile.nationality')}</span>
              <input className="input" value={form.nationality} onChange={set('nationality')} />
            </label>
            <label className="field">
              <span className="field-label">{t('profile.birthDate')}</span>
              <input className="input" type="date" value={form.birthDate || ''} onChange={set('birthDate')} />
            </label>
          </div>
          <label className="field">
            <span className="field-label">{t('profile.iban')}</span>
            <input className="input mono" value={form.iban} onChange={set('iban')} placeholder="CH00 0000 0000 0000 0000 0" />
          </label>
          <label className="field">
            <span className="field-label">{t('profile.phone')}</span>
            <input className="input" value={form.phone} onChange={set('phone')} placeholder="+41 79 000 00 00" />
          </label>

          {error && <div className="alert danger">{error}</div>}
          <div>
            <Button variant="primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? `${t('common.save')}…` : t('common.save')}
            </Button>
          </div>
        </form>
      </Card>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('profile.title')}</h1>
          <p className="muted">{t('profile.subtitle')}</p>
        </div>
      </header>
      <ProfileForm />
      <PasswordChangeCard />
    </section>
  );
}
